import React, { useEffect, useState } from 'react';
import { useTranslation } from "react-i18next";
import { useParams, useNavigate } from 'react-router-dom';
import './DeviceDetail.css';
import { fetchDeviceDetail } from '../../services/deviceService';
import { getItem, logout } from "../../utils/authStorage";
import Sidebar from "../shared/Sidebar";
import Header from "../shared/Header";
import LoadingSpinner from "../shared/LoadingSpinner";
import { useTheme } from "../../contexts/ThemeContext";
import {
  TablerOffline,
  TablerArrowLeft,
  TablerDrone,
  TablerSensor,
  TablerCamera,
  TablerInbox
} from "../shared/Icons";
import { useDeviceWebSocket } from "../../contexts/WebSocketContext";

const OnlineIcon = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: '6px', flexShrink: 0 }}>
    <path d="M20 6L9 17l-5-5" />
  </svg>
);

// Custom date formatter: DD/M/YYYY H:MM AM/PM
const formatDate = (isoString) => {
  if (!isoString) return "—";
  try {
    const date = new Date(isoString);
    if (isNaN(date.getTime())) return "—";
    const day = date.getDate();
    const month = date.getMonth() + 1;
    const year = date.getFullYear();
    let hours = date.getHours();
    const minutes = date.getMinutes();
    const ampm = hours >= 12 ? 'PM' : 'AM';
    hours = hours % 12;
    hours = hours ? hours : 12;
    const minutesStr = minutes < 10 ? '0' + minutes : minutes;
    return `${day}/${month}/${year} ${hours}:${minutesStr} ${ampm}`;
  } catch (e) {
    return "—";
  }
};

// Metadata date formatter: installed 26 May 05:30 am
const formatInstalledDate = (isoString) => {
  if (!isoString) return "—";
  try {
    const date = new Date(isoString);
    if (isNaN(date.getTime())) return "—";
    const day = date.getDate();
    const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    const month = monthNames[date.getMonth()];
    let hours = date.getHours();
    const minutes = date.getMinutes();
    const ampm = hours >= 12 ? 'pm' : 'am';
    hours = hours % 12;
    hours = hours ? hours : 12;
    const hourStr = hours < 10 ? '0' + hours : hours;
    const minutesStr = minutes < 10 ? '0' + minutes : minutes;
    return `installed ${day} ${month} ${hourStr}:${minutesStr} ${ampm}`;
  } catch (e) {
    return "—";
  }
};

function DeviceDetail() {
  const { t } = useTranslation();
  const { deviceId } = useParams();
  const [deviceData, setDeviceData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [collapsed, setCollapsed] = useState(false);
  
  const username = getItem("username");
  const role = (getItem("role") || "operator").toLowerCase();
  const { subscribe } = useDeviceWebSocket();
  const navigate = useNavigate();
  const { isDarkMode } = useTheme();

  const handleSignOut = () => {
    logout();
  };

  const hasRoleFn = (allowedRoles) => {
    const storedRole = (getItem("role") || '').toLowerCase();
    return allowedRoles.flat().some(r => typeof r === 'string' && r.toLowerCase() === storedRole);
  };

  useEffect(() => {
    async function loadDeviceDetail() {
      try {
        const response = await fetchDeviceDetail(deviceId);
        setDeviceData(response);
      } catch (err) {
        console.error("Failed to fetch device detail:", err);
      } finally {
        setLoading(false);
      }
    }
    loadDeviceDetail();
  }, [deviceId]);

  useEffect(() => {
    const unsub = subscribe((raw) => {
      const type = raw?.type;
      const data = raw?.message || raw?.payload || raw?.data;
      if (type === "device.status.active" || type === "device.status.inactive" || type === "device.status.streaming") {
        // Payload is now { devices: [[name, id], ...], updated_at } — fall back to raw array for safety
        const devicesList = Array.isArray(data?.devices) ? data.devices : (Array.isArray(data) ? data : []);
        const [, deviceUuid] = devicesList[0] || [];
        if (deviceUuid && deviceUuid === deviceId) {
          (async () => {
            try {
              const response = await fetchDeviceDetail(deviceUuid);
              setDeviceData(response);
            } catch (err) {
              console.error("Failed to fetch device detail:", err);
            } finally {
              setLoading(false);
            }
          })();
        }
      }
    });
    return unsub;
  }, [subscribe, deviceId]);


  const SectionTable = ({ title, devices, status }) => {
    const isOnline = status === "ACTIVE" || status === "STREAMING";
    const isCreated = status === "CREATED";
    const headerIcon = () => {
      if (title === "Cameras") return <TablerCamera color="var(--text-muted)" size={16} style={{ marginRight: '8px', flexShrink: 0 }} />;
      if (title === "Drones") return <TablerDrone color="var(--text-muted)" size={16} style={{ marginRight: '8px', flexShrink: 0 }} />;
      return <TablerSensor color="var(--text-muted)" size={16} style={{ marginRight: '8px', flexShrink: 0 }} />;
    };

    return (
      <div className="device-section">
        <div className="section-header">
          {headerIcon()}
          <span className="section-title">{title.toUpperCase()}</span>
          <span className="section-badge">{devices.length} device</span>
        </div>

        {devices.length > 0 ? (
          <div className="table-responsive">
            <table className="devicedetail-table">
              <thead>
                <tr>
                    <th>{t("label.name")}</th>
                  <th>{t("label.type")}</th>
                  <th>{t("label.statusLabel")}</th>
                  <th>Installed</th>
                </tr>
              </thead>
              <tbody>
                {devices.map((item) => (
                  <tr key={item.id}>
                    <td>{item.name || "N/A"}</td>
                    <td>{item.camera_type || item.device_type || title.slice(0, -1).toUpperCase()}</td>
                    <td>
                      <span
                        className={`status-badge-table ${isOnline ? "status-active" : isCreated ? "status-created" : "status-inactive"}`}
                      >
                        {isOnline ? (
                          <>
                            <OnlineIcon /> {t("status.active")}
                          </>
                        ) : isCreated ? (
                          <>
                            <TablerOffline size={18} style={{ marginRight: '6px', flexShrink: 0 }} /> {t("status.created")}
                          </>
                        ) : (
                          <>
                            <TablerOffline size={18} style={{ marginRight: '6px', flexShrink: 0 }} /> {t("status.offline")}
                          </>
                        )}
                      </span>
                    </td>
                    <td>{item.installation_date ? formatDate(item.installation_date) : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="empty-card">
            <div className="empty-icon">
              <TablerInbox color="var(--text-muted)" size={20} />
            </div>
            <div className="empty-text">{t("label.noDevicesFound")}</div>
            <div className="empty-subtext">{t("label.noDevicesFound")}</div>
          </div>
        )}
      </div>
    );
  };

  const isDeviceOnline = deviceData?.status === "ACTIVE" || deviceData?.status === "STREAMING";
  const isDeviceCreated = deviceData?.status === "CREATED";

  return (
    <div className={`device-detail-page ${isDarkMode ? "dark" : "light"}`}>
      <Sidebar
        collapsed={collapsed}
        onToggleCollapse={() => setCollapsed(prev => !prev)}
        isDarkMode={isDarkMode}
        username={username}
        role={role}
        onLogout={handleSignOut}
        hasRole={hasRoleFn}
      />

      <div className="main-content">
        <Header title={t("label.deviceDetails")} />
        <div className="main-divider" />

        <div className="device-detail-container">
          {loading ? (
            <LoadingSpinner message={t("loading.deviceDetails")} />
          ) : !deviceData ? (
            <div className="device-loading-state">{t("label.noDeviceData")}</div>
          ) : (
            <>
              {/* Header Card */}
              <div className="device-detail-header-card">
                <div className="header-card-left">
                  <button className="back-btn" onClick={() => navigate("/device-list")} title={t("button.backToList")}>
                    <TablerArrowLeft size={16} />
                  </button>
                  <div className="header-info-group">
                    <h2 className="device-detail-name">{deviceData.name || "Device Detail"}</h2>
                    <span className="device-detail-meta">
                      {typeof deviceData.type === 'string' && deviceData.type.length > 0 ? (deviceData.type.charAt(0).toUpperCase() + deviceData.type.slice(1).toLowerCase()) : "Drone"} &bull; {formatInstalledDate(deviceData.created_at)}
                    </span>
                  </div>
                </div>
                <span
                  className={`status-badge-large ${isDeviceOnline ? "status-active" : isDeviceCreated ? "status-created" : "status-inactive"}`}
                >
                  {isDeviceOnline ? (
                    <>
                      <OnlineIcon /> Active
                    </>
                  ) : isDeviceCreated ? (
                    <>
                      <TablerOffline size={18} style={{ marginRight: '6px', flexShrink: 0 }} /> Created
                    </>
                  ) : (
                    <>
                      <TablerOffline size={18} style={{ marginRight: '6px', flexShrink: 0 }} /> Offline
                    </>
                  )}
                </span>
              </div>

              {/* Section lists */}
              <div className="device-sections-list">
                <SectionTable title="Cameras" devices={deviceData.cameras || []} status={deviceData.status} />
                <SectionTable title="Drones" devices={deviceData.drones || []} status={deviceData.status} />
                <SectionTable title="Sensors" devices={deviceData.sensors || []} status={deviceData.status} />
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default DeviceDetail;